import styled from 'styled-components';
import { patternOnsetBeats, RhythmInput } from '../../../../../common/src/rhythm_echo.ts';
import { ButtonColumn, PrimaryButton, SecondaryButton, TextButton } from '../learn_buttons.tsx';
import { allLights, lightsForPlayback, lightsFromResult, RhythmBlocks, RhythmLight } from './rhythm_blocks.tsx';
import {
  canRetry,
  replayOnsetIndices,
  replayTapBeatIndex,
  RhythmReplayState,
  RhythmResultState,
} from './rhythm_echo_session.ts';
import { RhythmNotation } from './rhythm_notation.tsx';
import { RhythmResultLegend } from './rhythm_result_legend.tsx';
import {
  PatternProgressRow,
  RowLabel,
  ScreenBody,
  ScreenSubtitle,
  ScreenTitle,
  Slack,
} from './rhythm_screen_parts.tsx';
import { RhythmTapNotation } from './rhythm_tap_notation.tsx';

export const rhythmResultScreenId = 'rhythm-result';
export const rhythmNextButtonId = 'rhythm-next-button';
export const rhythmReplayButtonId = 'rhythm-replay-button';
export const rhythmRetryButtonId = 'rhythm-retry-button';
export const rhythmPlayedRowTestId = 'rhythm-played-row';
export const rhythmTappedRowTestId = 'rhythm-tapped-row';

// The per-pattern result (issue #3473), from the "Rhythm Echo (result)" artboard: the headline
// ("Nice, 4 of 5") and hint, "What we played" fully lit with the notation under it, "What you
// tapped" in the result colours with the tap notation under it (the taps as notes where they
// landed, against ghosts of the expected notes, so an early or late tap shows which way it went),
// the legend, then Next pattern (See results after the last) and Hear both again, with a Try again
// text link under them while the pattern can still be retried (once; the link is gone after
// that). During a replay both rows light in time with the sounds instead: the played row onset by
// onset, the tapped row beat by beat and the tap notation tap by tap as each recorded tap sounds.
// For a sung session (state.input 'voice', Sing Rhythm, issue #3723) the second row is "What you
// sang", and it lights the same way while the singer's own take plays.
export const RhythmResultScreen = ({
  state,
  onNext,
  onReplay,
  onRetry,
}: {
  state: RhythmResultState | RhythmReplayState;
  onNext: () => void;
  onReplay: () => void;
  onRetry: () => void;
}) => {
  const { result } = state;
  const onsetCount = patternOnsetBeats(result.pattern).length;
  const isLast = state.patternIndex + 1 >= state.patterns.length;
  const replaying = state.phase === 'replay';
  const playedLights = replaying
    ? lightsForPlayback(onsetCount, replayOnsetIndices(state).pattern)
    : allLights(onsetCount, 'on');
  const tappedLights = replaying
    ? lightsUpTo(lightsFromResult(result), replayTapBeatIndex(state))
    : lightsFromResult(result);
  const tapsSounded = replaying ? (replayOnsetIndices(state).tap ?? -1) : undefined; // -1: none yet
  return (
    <ScreenBody id={rhythmResultScreenId} data-phase={state.phase}>
      <PatternProgressRow index={state.patternIndex} count={state.patterns.length} level={state.level} />
      <ScreenTitle>{result.headline}</ScreenTitle>
      <ScreenSubtitle>{result.hint}</ScreenSubtitle>
      <Row data-testid={rhythmPlayedRowTestId}>
        <RowLabel>What we played</RowLabel>
        <RhythmBlocks pattern={result.pattern} lights={playedLights} />
        <Notation>
          <RhythmNotation pattern={result.pattern} />
        </Notation>
      </Row>
      <Row data-testid={rhythmTappedRowTestId}>
        <RowLabel>{givenBackLabel(state.input)}</RowLabel>
        <RhythmBlocks pattern={result.pattern} lights={tappedLights} />
        <Notation>
          <RhythmTapNotation
            pattern={result.pattern}
            level={state.level}
            tapsMs={result.tapsMs}
            highlightUpTo={tapsSounded}
          />
        </Notation>
      </Row>
      <Legend>
        <RhythmResultLegend />
      </Legend>
      <Slack />
      <ButtonColumn>
        <PrimaryButton id={rhythmNextButtonId} data-testid={rhythmNextButtonId} onClick={onNext}>
          {isLast ? 'See results' : 'Next pattern'}
        </PrimaryButton>
        <SecondaryButton id={rhythmReplayButtonId} data-testid={rhythmReplayButtonId} onClick={onReplay}>
          Hear both again
        </SecondaryButton>
      </ButtonColumn>
      <RetryRow>
        {canRetry(state) && (
          <TextButton
            id={rhythmRetryButtonId}
            data-testid={rhythmRetryButtonId}
            tabIndex={-1}
            onPointerDown={(event) => event.preventDefault()} // never focused, so Enter (Next) and the space bar cannot press it
            onClick={onRetry}
          >
            Try again
          </TextButton>
        )}
      </RetryRow>
    </ScreenBody>
  );
};

export function givenBackLabel(input: RhythmInput = 'tap'): string {
  return input === 'voice' ? 'What you sang' : 'What you tapped';
}

// The result colours up to and including beat `litIndex`, the rest off (a replay in progress)
function lightsUpTo(lights: RhythmLight[], litIndex: number | undefined): RhythmLight[] {
  return lights.map((light, index) => (litIndex !== undefined && index <= litIndex ? light : 'off'));
}

const shortScreen = '@media (max-height: 700px)';

const Row = styled.div`
  margin-top: 18px;

  ${shortScreen} {
    margin-top: 12px;
  }
`;

const Notation = styled.div`
  margin-top: 8px;
`;

const Legend = styled.div`
  margin-top: 14px;
`;

// Reserves the Try again link's line whether or not it is offered, so the buttons above stay put
const RetryRow = styled.div`
  display: flex;
  justify-content: center;
  height: 32px;
  margin-top: 2px;
`;
